(() => {
  if (customElements.get('wavs-ear-designer')) return;
  const normalize = value => String(value || '').trim().toLowerCase().replace(/[\s_-]+/g, ' ');
  const isCustomDesign = value => /^custom design(?:$|\s|\()/.test(normalize(value));
  const defaults = () => ({ size: 100, rotation: 0, horizontal: 0, vertical: 0, fit: 'cover' });
  const caps = {
    left: { cropX: 30, centerX: 280, width: 394, height: 469, path: new Path2D('M113 561 C121 493 190 425 285 406 C357 389 440 409 491 462 C502 502 509 555 498 608 C484 640 462 674 446 711 C429 748 420 786 409 824 C399 855 380 869 359 869 C325 869 288 834 249 798 C193 748 152 710 134 664 C119 628 108 592 113 561 Z') },
    right: { cropX: 670, centerX: 310, width: 391, height: 469, path: new Path2D('M795 463 C850 416 927 392 994 403 C1080 417 1147 468 1168 533 C1182 573 1175 607 1161 641 C1140 688 1107 729 1064 769 C1011 817 966 864 937 869 C911 874 894 862 881 837 C866 805 857 764 843 727 C829 687 811 653 796 624 C779 590 779 555 785 518 C787 497 790 478 795 463 Z') }
  };
  class WavsEarDesigner extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.form = document.getElementById(this.dataset.formId);
      if (!this.form) {
        this.timer = setTimeout(() => this.connectedCallback(), 50);
        return;
      }
      this.initialized = true;
      this.info = this.closest('product-info') || this.closest('.shopify-section');
      this.messages = this.querySelector('[data-strings]').dataset;
      this.status = this.querySelector('[role=status]');
      this.placement = this.querySelector('[data-placement]');
      this.copyButton = this.querySelector('[data-copy]');
      this.controller = new AbortController();
      this.ears = [...this.querySelectorAll('[data-ear]')].map(card => ({
        card, side: card.dataset.ear, canvas: card.querySelector('canvas'),
        input: card.querySelector('[data-original]'), preview: card.querySelector('[data-preview]'),
        values: defaults(), revision: 0, loading: false
      }));
      const listen = (target, type, handler, capture = false) => target.addEventListener(type, handler, { signal: this.controller.signal, capture });
      this.form.enctype = 'multipart/form-data';
      listen(this.form, 'submit', event => this.submit(event), true);
      listen(this.form, 'variant:change', event => {
        this.pending = false;
        this.setActive((event.detail.variant?.options || []).some(value => isCustomDesign(value)));
      });
      listen(this.info, 'change', event => {
        if (!event.target.closest('variant-picker')) return;
        this.pending = true;
        this.syncFromPicker();
      }, true);
      this.observer = new MutationObserver(() => this.syncFromPicker());
      this.observer.observe(this.info, { childList: true, subtree: true });
      listen(this.copyButton, 'click', () => this.copy());
      for (const ear of this.ears) {
        listen(ear.input, 'change', () => this.load(ear));
        for (const range of ear.card.querySelectorAll('[data-control]')) {
          listen(range, 'input', () => {
            ear.values[range.dataset.control] = range.dataset.control === 'fit' ? range.value : Number(range.value);
            ear.revision++;
            this.draw(ear);
          });
        }
        listen(ear.card.querySelector('[data-reset]'), 'click', () => {
          ear.values = defaults(); ear.revision++; this.draw(ear);
        });
        listen(ear.canvas, 'pointerdown', event => {
          if (!ear.image || !this.active || this.preparing) return;
          const box = ear.canvas.getBoundingClientRect();
          ear.drag = { id: event.pointerId, x: event.clientX, y: event.clientY, box, ...ear.values };
          ear.canvas.setPointerCapture(event.pointerId);
        });
        listen(ear.canvas, 'pointermove', event => {
          if (!ear.drag || event.pointerId !== ear.drag.id) return;
          const clamp = n => Math.min(100, Math.max(-100, Math.round(n)));
          ear.values.horizontal = clamp(ear.drag.horizontal + (event.clientX - ear.drag.x) / ear.drag.box.width * 200);
          ear.values.vertical = clamp(ear.drag.vertical + (event.clientY - ear.drag.y) / ear.drag.box.height * 200);
          ear.revision++; this.draw(ear);
        });
        listen(ear.canvas, 'pointerup', () => { ear.drag = null; });
        listen(ear.canvas, 'pointercancel', () => { ear.drag = null; });
        listen(ear.canvas, 'keydown', event => {
          if (!ear.image || !this.active || !event.key.startsWith('Arrow')) return;
          event.preventDefault();
          const amount = event.shiftKey ? 10 : 2;
          const axis = ['ArrowLeft', 'ArrowRight'].includes(event.key) ? 'horizontal' : 'vertical';
          const direction = ['ArrowLeft', 'ArrowUp'].includes(event.key) ? -1 : 1;
          ear.values[axis] = Math.max(-100, Math.min(100, ear.values[axis] + amount * direction));
          ear.revision++; this.draw(ear);
        });
        this.draw(ear);
      }
      this.baseImage = new Image();
      this.baseImage.crossOrigin = 'anonymous';
      this.baseImage.src = this.dataset.baseImage;
      this.baseReady = this.baseImage.decode().then(() => {
        this.baseLoaded = true;
        this.ears.forEach(ear => this.draw(ear));
        return true;
      }).catch(() => { this.say('baseError', true); return false; });
      this.setActive(this.dataset.initialActive === 'true');
      this.syncFromPicker();
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      this.controller?.abort(); this.observer?.disconnect();
      this.initialized = false;
    }
    syncFromPicker() {
      const picker = this.info.querySelector('variant-picker');
      if (!picker) return;
      const selected = [...picker.querySelectorAll('select, input:checked')].map(input => input.value);
      this.setActive(selected.some(value => isCustomDesign(value)));
    }
    setActive(active) {
      this.active = active; this.hidden = !active;
      this.form.dataset.wavsDesignActive = String(active);
      for (const input of this.querySelectorAll('input[name]')) input.disabled = !active;
      this.copyButton.disabled = !active || !this.ears[0].image || this.preparing;
      for (const ear of this.ears) {
        for (const control of ear.card.querySelectorAll('[data-control], [data-reset]')) control.disabled = !active || !ear.image || this.preparing;
      }
      if (!active) this.say('');
    }
    say(key, error = false) {
      this.status.textContent = this.messages[key] || '';
      this.status.dataset.error = String(error);
    }
    async load(ear) {
      const file = ear.input.files[0];
      const revision = ++ear.revision;
      ear.image = null; ear.file = null; ear.loading = false; ear.preview.value = '';
      this.draw(ear); this.setActive(this.active);
      ear.card.querySelector('[data-filename]').textContent = '';
      if (!file) { this.draw(ear); this.setActive(this.active); return; }
      if (!['image/jpeg', 'image/png'].includes(file.type) || file.size > 10 * 1024 * 1024) {
        ear.input.value = ''; this.say('invalid', true); this.draw(ear); this.setActive(this.active); return;
      }
      ear.loading = true;
      const url = URL.createObjectURL(file);
      try {
        const img = new Image();
        img.src = url; await img.decode();
        if (revision !== ear.revision) return;
        if (img.naturalWidth * img.naturalHeight > 24000000) {
          ear.input.value = ''; this.say('large', true); return;
        }
        ear.image = img; ear.file = file; ear.values = defaults();
        ear.card.querySelector('[data-filename]').textContent = file.name;
        this.say('');
      } catch {
        if (revision === ear.revision) { ear.input.value = ''; this.say('decode', true); }
      } finally {
        URL.revokeObjectURL(url);
        if (revision === ear.revision) { ear.loading = false; this.draw(ear); this.setActive(this.active); }
      }
    }
    putFile(input, file) {
      const transfer = new DataTransfer(); transfer.items.add(file); input.files = transfer.files;
    }
    copy() {
      const [left, right] = this.ears;
      if (!left.image || this.preparing) return;
      try {
        this.putFile(right.input, left.file);
        right.file = left.file; right.image = left.image;
        right.values = { ...left.values }; right.revision++; right.loading = false;
        right.card.querySelector('[data-filename]').textContent = left.file.name;
        this.draw(right); this.setActive(this.active); this.say('');
      } catch { this.say('unsupported', true); }
    }
    draw(ear, canvas = ear.canvas, production = false) {
      const ctx = canvas.getContext('2d');
      const cap = caps[ear.side];
      ctx.clearRect(0, 0, 600, 720);
      if (this.baseLoaded) ctx.drawImage(this.baseImage, cap.cropX, 285, 600, 720, 0, 0, 600, 720);
      if (ear.image && this.baseLoaded) {
        ctx.save();
        ctx.translate(-cap.cropX, -285);
        ctx.clip(cap.path);
        ctx.translate(cap.cropX, 285);
        const v = ear.values;
        ctx.translate(cap.centerX + v.horizontal * 3, 350 + v.vertical * 3.6);
        ctx.rotate(v.rotation * Math.PI / 180);
        const fitMethod = v.fit === 'contain' ? Math.min : Math.max;
        const fit = fitMethod(cap.width / ear.image.naturalWidth, cap.height / ear.image.naturalHeight) * v.size / 100;
        const w = ear.image.naturalWidth * fit, h = ear.image.naturalHeight * fit;
        ctx.drawImage(ear.image, -w / 2, -h / 2, w, h);
        ctx.restore();
      }
      if (production) return;
      ear.card.querySelector('.wavs-designer__empty').hidden = !!ear.image;
      for (const input of ear.card.querySelectorAll('[data-control]')) {
        const key = input.dataset.control;
        input.value = ear.values[key];
        if (key === 'fit') continue;
        ear.card.querySelector(`[data-output="${key}"]`).textContent = `${ear.values[key]}${key === 'rotation' ? '°' : '%'}`;
      }
    }
    async submit(event) {
      if (!this.active) return;
      if (this.resubmitting) { this.resubmitting = false; return; }
      event.preventDefault(); event.stopImmediatePropagation();
      if (this.preparing) return;
      const fail = key => { this.say(key, true); this.status.focus(); };
      if (this.pending) { fail('changed'); return; }
      if (this.ears.some(ear => ear.loading)) { fail('busy'); return; }
      if (this.ears.some(ear => !ear.image || !ear.file)) { fail('missing'); return; }
      const revisions = this.ears.map(ear => ear.revision);
      const variantId = this.form.querySelector('[name=id]').value;
      const submitter = event.submitter;
      this.preparing = true; this.say('preparing'); this.setActive(this.active);
      try {
        if (!await this.baseReady) { fail('baseError'); return; }
        const previews = await Promise.all(this.ears.map(async ear => {
          const canvas = document.createElement('canvas'); canvas.width = 600; canvas.height = 720;
          this.draw(ear, canvas, true);
          const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
          if (!blob) throw new Error('preview');
          return new File([blob], `WAVS-${ear.side}-placement.png`, { type: 'image/png' });
        }));
        if (!this.active || this.pending || variantId !== this.form.querySelector('[name=id]').value || this.ears.some((ear, i) => ear.revision !== revisions[i])) {
          fail('changed'); return;
        }
        this.ears.forEach((ear, i) => this.putFile(ear.preview, previews[i]));
        this.placement.value = JSON.stringify({ version: 2, canvas: { width: 600, height: 720 }, productId: this.dataset.productId, variantId, faceplate: 'wavs-blank-iems-v2', ears: Object.fromEntries(this.ears.map(ear => [ear.side, { ...ear.values, filename: ear.file.name, imageWidth: ear.image.naturalWidth, imageHeight: ear.image.naturalHeight }])) });
        this.say('ready'); this.resubmitting = true;
        this.form.requestSubmit(submitter?.isConnected ? submitter : undefined);
        this.resubmitting = false;
      } catch { fail('failed'); }
      finally { this.preparing = false; this.setActive(this.active); }
    }
  }
  customElements.define('wavs-ear-designer', WavsEarDesigner);
})();
